import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseProduct = API_JSON & {
  name: string;
  alternativeName: string | null;
  identifier: ProductIdentifier;
  description: string;
  enabled: boolean;
  areas: API_JSON[];
};

export type ProductIdentifier = 'wellbeing' | 'trm' | 'pat' | 'ipt' | 'engagement';

const groups = [
  'product-name',
  'product-identifier',
  'product-description',
  'product-areas',
  'enabled'
];

/**
 * Fetch products
 * @param params can be used to customise/limit the products retrieved
 * @returns an array of products
 */
export const getProducts: GetMany<BaseProduct> = async (params) =>
  (
    await ASSIST_API.get('products', {
      params: requestParams({ groups }, params)
    })
  ).data;

/**
 * Fetch product (singular)
 * @param params can be used to customise the product retrieved
 * @returns a single product
 */
export const getProduct: GetOne<BaseProduct> = async (id, params) =>
  (await ASSIST_API.get(`products/${id}`, { params })).data;

/**
 * Hook implementation to fetch products
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns products matching request
 */
export const useProducts: AssistQuery.UseMany<BaseProduct> = (params, options) =>
  useQueryAssist(['products', params], () => getProducts(params), {
    staleTime: STALE_MEDIUM,
    allowNoFilters: true,
    ...options
  });

/**
 * Given an productId fetch and return the product
 *  - only runs once productId is provided
 *  - fetches the one product
 *  - caches data
 * @param id productId
 * @param params optional parameters to change requested data
 * @returns the matching product
 */
export const useProduct: AssistQuery.UseOne<BaseProduct> = (id, params, options) =>
  useQueryAssist(['product', { id, ...params }], () => getProduct(id, params), {
    staleTime: STALE_MEDIUM,
    ...options,
    enabled: !!id
  });
